import { useLoading } from '@teko/ds-desktop';
import { ISupermarketTerminal } from '@teko/order';
import { trackCustomEvent } from '@teko/tracking';
import TerraKit from '@terra-js/terra-kit';
import { PopupTerminalPicker } from 'components/Terminal';
import { useGlobalData } from 'contexts/GlobalDataContext';
import { useServices } from 'contexts/ServiceContext';
import {
  clearCacheAndReloadPage,
  getJson,
  getLocalStorageValue,
  getUserSelectedInfo,
  reloadPage,
  removeUserSelectedInfo,
  setLocalStorageValue,
  setUserSelectedInfo,
} from 'helpers';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { userSelectedConstants } from '../../constants';

export enum MODE_TERMINAL_PICKER {
  ENTER_ADDRESS = 'ENTER_ADDRESS',
  LIST_TERMINAL = 'LIST_TERMINAL',
  LIST_ADDRESS = 'LIST_ADDRESS',
}

export interface DistanceTerminalContext {
  currentTerminal?: ISupermarketTerminal;
  currentTerminals?: ISupermarketTerminal[];
  locations?: Location[];
  location?: Location;
  setLoadingGetDistanceTerminals?: (loading: boolean) => void;
  isLoadingGetDistanceTerminals?: boolean;
  setLocationGetDistanceTerminals?: (location: Location) => void;
  locationGetDistanceTerminals?: Location;
  isGetCurrentLocation?: boolean;
  setIsGetCurrentLocation?: (isGetCurrentLocation: boolean) => void;
  isModeEnterAddress?: boolean;
  setIsModeEnterAddress?: (isModeEnterAddress: boolean) => void;

  modeTerminalPicker?: MODE_TERMINAL_PICKER;
  setModeTerminalPicker?: (modeTerminalPicker: MODE_TERMINAL_PICKER) => void;

  showTerminalPicker?: boolean;
  onCloseTerminalPicker?: () => void;
  onOpenTerminalPicker?: () => void;
  getDistanceLocation?: (addressInfo: {
    address?: string;
    lat?: number;
    long?: number;
  }) => Promise<ISupermarketTerminal[]>;
  setNewTerminal?: (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  messageError?: string;
  setMessageError?: Dispatch<SetStateAction<string>>;
  setLoadingGetDistanceTerminal?: (loading: boolean) => void;
  overrideTerminal?: (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  permissionGetLocationStatus?: string;
  temporaryDataDistanceTerminal: TemporaryDataTerminalPicker;
  setTemporaryDataDistanceTerminal?: Dispatch<
    SetStateAction<TemporaryDataTerminalPicker>
  >;
}
export interface Location {
  address: string;
  provinceCode: string;
  districtCode?: string;
  wardCode: string;
  provinceName: string;
  wardName: string;
  districtName?: string;
  lat?: number;
  long?: number;
  node?: string;
}

export interface TemporaryDataTerminalPicker {
  location: Location;
  locations: Location[];
  terminals: ISupermarketTerminal[];
}

const defaultTemporaryData = {
  location: {
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
  },
  terminals: [],
  locations: [],
};

const DistanceTerminalContext = createContext<DistanceTerminalContext>({
  temporaryDataDistanceTerminal: defaultTemporaryData,
});

interface DistanceTerminalProviderProps {
  children: React.ReactNode;
}

export const DistanceTerminalProvider = ({
  children,
}: DistanceTerminalProviderProps) => {
  const {
    epicOmni1825,
    isDmsFlow,
    platformId,
    isMobile,
    clearCacheAfterSwitchLocation,
    isAllowGetTerminalFromTerra,
    terminalId,
  } = useGlobalData();
  const { orderService, cartServiceV2 } = useServices();

  const [, setLoadingGetDistanceTerminal] = useLoading('distanceTerminal');
  const [isModeEnterAddress, setIsModeEnterAddress] = useState(true);

  const [currentTerminals, setCurrentTerminals] = useState<
    ISupermarketTerminal[]
  >([]);
  const [showTerminalPicker, setShowTerminalPicker] = useState<boolean>(false);
  // epic omni 1825
  const [currentTerminal, setCurrentTerminal] = useState<
    ISupermarketTerminal | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('denied');
  const [location, setLocation] = useState<Location>({
    address: '',
    provinceCode: '',
    wardCode: '',
    provinceName: '',
    wardName: '',
    districtCode: '',
    districtName: '',
  });
  const [locations, setLocations] = useState<Location[]>([]);

  const [modeTerminalPicker, setModeTerminalPicker] =
    useState<MODE_TERMINAL_PICKER>(MODE_TERMINAL_PICKER.ENTER_ADDRESS);
  const [isGetCurrentLocation, setIsGetCurrentLocation] = useState(false);
  const [temporaryData, setTemporaryData] =
    useState<TemporaryDataTerminalPicker>(defaultTemporaryData);
  const [messageError, setMessageError] = useState('');

  const onOpenTerminalPicker = () => {
    setShowTerminalPicker(true);
    setMessageError('');
  };

  const onCloseTerminalPicker = () => {
    setShowTerminalPicker(false);
  };

  const selectTerminal = (terminal: ISupermarketTerminal) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(temporaryData.location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(temporaryData.terminals),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATIONS,
      JSON.stringify(temporaryData.locations),
      isMobile,
    );
    setUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      JSON.stringify(isGetCurrentLocation),
      isMobile,
      true,
    );
    setCurrentTerminal(terminal);
  };

  const overrideTerminal = async (
    location: Location,
    terminal: ISupermarketTerminal,
    terminals: ISupermarketTerminal[],
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      JSON.stringify(location),
      isMobile,
    );
    setLocalStorageValue(
      userSelectedConstants.TERMINALS,
      JSON.stringify(terminals),
      isMobile,
    );
    setUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      JSON.stringify(false),
      isMobile,
      true,
    );
    setLocation(location);
    setCurrentTerminal(terminal);
    setCurrentTerminals(terminals);
    setIsGetCurrentLocation(false);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
    // Note: we need to update terminal in cart service to make sure the cart will be correct with the new terminal
    else {
      cartServiceV2.setParams({
        terminalId: terminal.terminalId,
        terminal: terminal.terminalCode,
      });
    }
  };

  const getDistanceLocation = async ({
    address,
    lat,
    long,
  }: {
    address?: string;
    lat?: number;
    long?: number;
  }) => {
    try {
      setLoading(true);
      const response = await orderService.getTerminalsByAddress({
        fullAddress: address,
        lat,
        long,
        platformId,
      });
      setLoading(false);
      return response.data.terminals;
    } catch (error) {
      setLoading(false);
      return [];
    }
  };

  const setNewTerminal = (
    terminal: ISupermarketTerminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    if (!terminal) {
      return;
    }
    selectTerminal(terminal);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  const getDataFromLocalStorage = () => {
    const terminalCodeFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      isMobile,
      true,
    );
    const terminalsFromStorage = getLocalStorageValue(
      userSelectedConstants.TERMINALS,
      isMobile,
    );
    const isGetGeoLocationFromStorage = getUserSelectedInfo(
      userSelectedConstants.IS_GET_GEOLOCATION,
      isMobile,
      true,
    );
    const jsonUserLocationFromStorage = getLocalStorageValue(
      userSelectedConstants.USER_LOCATION,
      isMobile,
    );

    const jsonUserLocationsFromStorage = getLocalStorageValue(
      userSelectedConstants.USER_LOCATIONS,
      isMobile,
    );

    return {
      terminalCodeFromStorage,
      terminalsFromStorage,
      isGetGeoLocationFromStorage,
      jsonUserLocationFromStorage,
      jsonUserLocationsFromStorage,
    };
  };

  const getAndSaveTerminalFromStorage = async () => {
    trackCustomEvent('Start get data from storage', {
      action: 'get data from terra',
      label: 'Huy Test',
    });
    if (TerraKit.isAvailable() && isAllowGetTerminalFromTerra) {
      trackCustomEvent(
        'Start get terminal Data FromApp when turn on flag 1584',
        {},
      );
      const terminalDataFromApp = await TerraKit.erp().getTerminal();
      trackCustomEvent('Terminal Data FromApp when turn on flag 1584', {
        action: 'get data from terra',
        label: JSON.stringify(terminalDataFromApp),
      });
      if (!!terminalDataFromApp && terminalId !== terminalDataFromApp.id) {
        trackCustomEvent(
          'Start save Terminal Data FromApp when turn on flag 1584',
          {
            action: 'get data from terra',
            label: `${terminalDataFromApp} + ${terminalDataFromApp.id}`,
          },
        );
        setNewTerminal({
          terminalCode: terminalDataFromApp.code,
          terminalId: terminalDataFromApp.id,
          terminalName: terminalDataFromApp.name || '',
          siteId: 0,
          fullAddress: '',
          distance: 0,
          provinceCode: '',
        });
      }
      return;
    }
    const {
      terminalCodeFromStorage,
      terminalsFromStorage,
      isGetGeoLocationFromStorage,
      jsonUserLocationFromStorage,
      jsonUserLocationsFromStorage,
    } = getDataFromLocalStorage();
    if (!!isGetGeoLocationFromStorage) {
      setIsGetCurrentLocation(getJson(isGetGeoLocationFromStorage));
    }
    if (!!terminalsFromStorage) {
      setCurrentTerminals(getJson(terminalsFromStorage) || []);
    }

    if (!!jsonUserLocationFromStorage) {
      const userLocationFromStorage = getJson(jsonUserLocationFromStorage);
      if (!!userLocationFromStorage) {
        setLocation(userLocationFromStorage);
      }
    }
    if (!!jsonUserLocationsFromStorage) {
      const userLocationsFromStorage = getJson(jsonUserLocationsFromStorage);
      if (!!userLocationsFromStorage) {
        setLocations(userLocationsFromStorage);
      }
    }
    if (!!terminalCodeFromStorage && !!terminalsFromStorage) {
      const terminals = getJson(terminalsFromStorage) || [];
      const terminal = terminals.find(
        (item: ISupermarketTerminal) =>
          item.terminalCode === terminalCodeFromStorage,
      );
      setCurrentTerminal(terminal);
    }
  };

  useEffect(() => {
    if ('geolocation' in navigator && 'permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setPermissionStatus(result.state); // 'granted', 'prompt', or 'denied'
        })
        .catch(() => {
          setPermissionStatus('denied');
        });
    } else {
      setPermissionStatus('denied');
    }
    const { jsonUserLocationFromStorage } = getDataFromLocalStorage();
    if (!isDmsFlow && epicOmni1825) {
      getAndSaveTerminalFromStorage();
    } else {
      // only remove location & terminal info and reload page If level location is set before (epicOmni1584 were enabled before)
      if (!!jsonUserLocationFromStorage && !epicOmni1825) {
        removeUserSelectedInfo(userSelectedConstants.TERMINAL, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_ID, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_NAME, isMobile);
        removeUserSelectedInfo(userSelectedConstants.USER_LOCATION, isMobile);
        reloadPage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epicOmni1825, isDmsFlow, isMobile]);

  return (
    <DistanceTerminalContext.Provider
      value={{
        setLoadingGetDistanceTerminals: setLoading,
        isLoadingGetDistanceTerminals: loading,
        setLocationGetDistanceTerminals: setLocation,
        locationGetDistanceTerminals: location,
        currentTerminal,
        currentTerminals,
        showTerminalPicker,
        onOpenTerminalPicker,
        onCloseTerminalPicker,
        isGetCurrentLocation,
        setIsGetCurrentLocation,
        getDistanceLocation,
        isModeEnterAddress,
        setIsModeEnterAddress,
        modeTerminalPicker,
        setModeTerminalPicker,
        setNewTerminal,
        messageError,
        setMessageError,
        setLoadingGetDistanceTerminal,
        overrideTerminal,
        permissionGetLocationStatus: permissionStatus,
        temporaryDataDistanceTerminal: temporaryData,
        setTemporaryDataDistanceTerminal: setTemporaryData,
        location,
        locations,
      }}
    >
      {children}
      {!isDmsFlow && !!epicOmni1825 && (
        <PopupTerminalPicker
          showPopup={showTerminalPicker}
          onClosePopup={onCloseTerminalPicker}
        />
      )}
    </DistanceTerminalContext.Provider>
  );
};

export const useDistanceTerminalContext = () => {
  return useContext(DistanceTerminalContext);
};
